import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle, MagnifyingGlassPlus } from "@phosphor-icons/react";

/**
 * Renders the physical signature captured on the mobile scanner when the
 * order was signed. Handles both SVG string payloads and image data URLs,
 * with a click-to-enlarge dialog and metadata (who + when).
 */
export default function SignatureViewer({ order }) {
  const [open, setOpen] = useState(false);
  const sig = order?.signature_svg;
  if (!sig) return null;

  const isSvg = typeof sig === "string" && sig.trim().startsWith("<");
  const isDataUrl = typeof sig === "string" && sig.startsWith("data:image");
  const isRawB64 = typeof sig === "string" && !isSvg && !isDataUrl && /^[A-Za-z0-9+/=]+$/.test(sig.slice(0, 200));
  const inferredSrc = isDataUrl ? sig : isRawB64 ? `data:image/png;base64,${sig}` : null;

  return (
    <>
      <div
        data-testid="signature-thumbnail"
        className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-800"
      >
        <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400 mb-2">
          <CheckCircle size={16} weight="fill" />
          <span className="font-semibold">Signed</span>
          {order.signed_by && <span className="text-zinc-600 dark:text-zinc-400">by {order.signed_by}</span>}
          {order.signed_at && (
            <span className="text-zinc-500 text-xs">· {new Date(order.signed_at).toLocaleString()}</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="signature-open-dialog"
          className="group relative inline-block rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:border-emerald-400 dark:hover:border-emerald-700 transition-colors overflow-hidden"
          title="Click to enlarge signature"
        >
          <div className="h-24 w-64 flex items-center justify-center p-2 bg-white">
            {isSvg ? (
              <div
                className="max-h-full max-w-full [&_svg]:max-h-full [&_svg]:max-w-full text-zinc-900"
                dangerouslySetInnerHTML={{ __html: sig }}
              />
            ) : inferredSrc ? (
              <img src={inferredSrc} alt="Signature" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-xs text-zinc-500">Signature format not recognised</span>
            )}
          </div>
          <span className="absolute inset-0 flex items-center justify-center bg-emerald-600/0 group-hover:bg-emerald-600/10 transition-colors">
            <MagnifyingGlassPlus size={20} weight="bold" className="text-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity" />
          </span>
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="signature-dialog">
          <DialogHeader>
            <DialogTitle>Signature — {order.order_number}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-zinc-600 dark:text-zinc-400 -mt-2">
            {order.signed_by && <span>Signed by <span className="font-semibold text-zinc-900 dark:text-zinc-100">{order.signed_by}</span></span>}
            {order.signed_at && <span> on {new Date(order.signed_at).toLocaleString()}</span>}
          </div>
          <div className="mt-4 p-6 bg-white rounded-md border border-zinc-200 flex items-center justify-center min-h-[200px]">
            {isSvg ? (
              <div
                className="max-h-[500px] max-w-full [&_svg]:max-h-[500px] [&_svg]:max-w-full text-zinc-900"
                dangerouslySetInnerHTML={{ __html: sig }}
              />
            ) : inferredSrc ? (
              <img src={inferredSrc} alt="Signature" className="max-h-[500px] max-w-full object-contain" />
            ) : (
              <span className="text-sm text-zinc-500">Signature format not recognised</span>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
